import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, Suspense, lazy, useEffect } from "react";
import { createRootRouteWithContext, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { hasSession, serverAuth } from "@/data/auth";
import { Banner } from "@offsilo/ui/banner";
import { BrandProvider } from "@offsilo/ui/brand";
import { serverMode } from "@/data/actions";
import { usePortalBrandMark } from "@/data/brand";
import { useSignInWorkspace } from "@/data/hooks";
import { currentPreview, setPreview } from "@/data/server-client";
import { m } from "@/paraglide/messages.js";

// the portal's tour host (and the tour engine under it) is a chunk of its own, fetched with the first portal page
const PortalTourHost = lazy(() => import("@/tours/PortalTourHost").then((mod) => ({ default: mod.PortalTourHost })));
// the pages a person sees before they have a session: no live connection is opened there
const PUBLIC_PAGE = /^\/(sign-in|trust|invite\/|portal\/sign-in)/;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // server mode: a client portal page needs a session before it draws, so a signed-out visitor lands on the portal sign-in
  // at once with the page as its return path (the team pages check the same in _app.tsx)
  beforeLoad: async ({ location }) => {
    const path = location.pathname;
    if (!serverAuth || !path.startsWith("/portal") || path.startsWith("/portal/sign-in")) return;
    if ((await hasSession()) !== false) return;
    throw redirect({ to: "/portal/sign-in", search: path === "/portal" || path === "/portal/" ? {} : { next: location.href } });
  },
  component: Root,
});

/** The app shell shows the "Preview as" banner itself; the portal has its own shells, so a portal preview (an owner
 *  looking through a client contact) gets the banner here, pinned to the bottom of the screen. */
function Root() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const preview = currentPreview();
  const portal = path.startsWith("/portal");
  const queryClient = useQueryClient();
  // live updates open once a signed-in shell is on screen (the staff and portal pages), never on the public pages
  const publicPage = PUBLIC_PAGE.test(path);
  useEffect(() => {
    if (!publicPage) void import("@/data/realtime").then((mod) => mod.startRealtime(queryClient));
  }, [publicPage, queryClient]);
  return (
    <>
      {portal ? (
        <PortalBrand>
          {/* the sign-in page has no portal tours to run, so it mounts no tour host */}
          {path.startsWith("/portal/sign-in") ? (
            <Outlet />
          ) : (
            <Suspense fallback={null}>
              <PortalTourHost audience="client">
                <Outlet />
              </PortalTourHost>
            </Suspense>
          )}
        </PortalBrand>
      ) : (
        <Outlet />
      )}
      {preview && portal && (
        <div className="fixed inset-x-0 bottom-0 z-(--z-pop) p-3">
          <Banner tone="accent" actionLabel={m.ui_exit_preview()} onAction={() => setPreview(null)} className="mx-auto max-w-3xl shadow-card">
            {m.ui_preview_banner({ who: preview.label })}
          </Banner>
        </div>
      )}
    </>
  );
}

/** Gives the portal's screens the agency's logo and brand colour (PortalShell reads the mark) and the agency's own web
 *  manifest (B211). Mounted only under /portal, so the other pages never read the signed-in workspace for it (X3-14). */
function PortalBrand({ children }: { children: ReactNode }) {
  usePortalManifest();
  return <BrandProvider mark={usePortalBrandMark()}>{children}</BrandProvider>;
}

/** Points the page's manifest at the workspace's portal manifest (name, brand colour and logo of the agency, start page
 *  /portal), so a client can install the portal as the agency's app. The server serves it without a session at
 *  /api/public/portal/<slug>/manifest.webmanifest. Leaving the portal puts the app's own manifest link back. */
function usePortalManifest(): void {
  const slug = useSignInWorkspace().data?.slug;
  useEffect(() => {
    if (!serverMode || !slug) return;
    const existing = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const link = existing ?? document.head.appendChild(Object.assign(document.createElement("link"), { rel: "manifest" }));
    const before = link.getAttribute("href");
    link.setAttribute("href", `/api/public/portal/${encodeURIComponent(slug)}/manifest.webmanifest`);
    return () => {
      if (!existing) link.remove();
      else if (before) link.setAttribute("href", before);
    };
  }, [slug]);
}
